/**
 * Validation Types
 *
 * TypeScript types for the multi-layer validation system.
 * Mirrors backend DTOs from app/modules/nib/validation/dtos.py
 *
 * Architecture:
 *   Employee Data → Validation Engine → ValidationResult
 *                        ↓
 *             ┌─────────────────────────────────┐
 *             │  FORMAT    │ BUSINESS │ CROSS_REF│
 *             │  ────────  │ ──────── │ ─────────│
 *             │  • NI# 8d  │ • Ceiling│ • Invalid│
 *             │  • Names   │ • Age/Cat│   NI# DB │
 *             │  • Wages   │ • Weeks  │ • Dupes  │
 *             └─────────────────────────────────┘
 *                        ↓
 *             List[ValidationIssue] → ValidationResult
 */

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Severity levels for validation issues.
 *
 * The severity determines how the issue affects submission:
 * - ERROR: Blocks C10 generation. Must be fixed before proceeding.
 * - WARNING: Allows generation with user acknowledgment.
 * - INFO: Informational only. No action required.
 */
export type ValidationSeverity = 'ERROR' | 'WARNING' | 'INFO';

/**
 * Categories for validation rules.
 *
 * Validation is organized into layers, executed in order:
 * 1. FORMAT: Syntax and structure validation (fast, no I/O)
 * 2. BUSINESS: Business rule validation (calculations, constraints)
 * 3. CROSS_REF: Cross-reference validation (database lookups, duplicates)
 */
export type ValidationCategory = 'FORMAT' | 'BUSINESS' | 'CROSS_REF';

// =============================================================================
// CORE TYPES
// =============================================================================

/**
 * A single validation issue found during validation.
 */
export interface ValidationIssue {
  /** Machine-readable issue code (e.g., 'FORMAT_NI_LENGTH') */
  code: string;
  /** Human-readable description of the issue */
  message: string;
  /** How serious the issue is */
  severity: ValidationSeverity;
  /** Which validation layer detected this */
  category: ValidationCategory;
  /** The field name that has the issue (e.g., 'employee_ni_number') */
  field?: string;
  /** The actual value that failed validation */
  value?: string;
  /** Optional suggested fix or correct value */
  suggestion?: string;
  /** NI# of the employee this issue relates to */
  employee_ni?: string;
  /** Additional context-specific information */
  metadata?: Record<string, unknown>;
}

/**
 * Validation result for a single employee record.
 */
export interface EmployeeValidationResult {
  /** The NI number of the validated employee */
  employee_ni: string;
  /** Full name for display */
  employee_name?: string;
  /** Whether this employee passes validation (no ERROR issues) */
  is_valid: boolean;
  /** Whether this employee has any warnings */
  has_warnings: boolean;
  /** Count of ERROR severity issues */
  error_count: number;
  /** Count of WARNING severity issues */
  warning_count: number;
  /** Count of INFO severity issues */
  info_count: number;
  /** List of all validation issues found */
  issues: ValidationIssue[];
  /** Timestamp of validation */
  validated_at: string;
  /** Row number in source file (for error reporting) */
  row_number?: number;
}

/**
 * Summary statistics for a submission validation.
 */
export interface ValidationSummary {
  /** Total number of employees validated */
  total_employees: number;
  /** Count of employees that passed validation */
  valid_employees: number;
  /** Count of employees that failed validation */
  invalid_employees: number;
  /** Total count of ERROR severity issues */
  total_errors: number;
  /** Total count of WARNING severity issues */
  total_warnings: number;
  /** Total count of INFO severity issues */
  total_info: number;
}

/**
 * Validation result for an entire submission (all employees).
 */
export interface SubmissionValidationResult {
  /** Employer NI registration number */
  employer_ni: string;
  /** Period in MMYY format (e.g., '0126') */
  contribution_period: string;
  /** C10 format type */
  format_type: 'HOSPITALITY' | 'NON_HOSPITALITY';
  /** Whether all employees pass validation (no ERROR issues) */
  is_valid: boolean;
  /** Whether C10 file can be generated */
  can_generate: boolean;
  /** Whether there are warnings requiring user acknowledgment */
  requires_acknowledgment: boolean;
  /** Summary statistics */
  summary: ValidationSummary;
  /** Submission-level issues (not tied to specific employee) */
  submission_issues: ValidationIssue[];
  /** Per-employee validation results */
  employee_results: EmployeeValidationResult[];
  /** Timestamp of validation */
  validated_at: string;
}

/**
 * Summary-only validation result (without full employee details).
 * Used for quick validation checks.
 */
export interface ValidationSummaryResult {
  employer_ni: string;
  contribution_period: string;
  format_type: 'HOSPITALITY' | 'NON_HOSPITALITY';
  is_valid: boolean;
  can_generate: boolean;
  requires_acknowledgment: boolean;
  total_employees: number;
  valid_employees: number;
  invalid_employees: number;
  total_errors: number;
  total_warnings: number;
  total_info: number;
  validated_at: string;
}

// =============================================================================
// NI VALIDATION TYPES
// =============================================================================

/**
 * Result of validating a single NI number.
 */
export interface NIValidationResult {
  /** The validated NI number */
  ni_number: string;
  /** Whether the NI number is valid */
  is_valid: boolean;
  /** Error messages if validation failed */
  errors: string[];
  /** Warning messages */
  warnings: string[];
  /** Machine-readable error codes */
  error_codes: string[];
  /** Suggested correct values (for typos) */
  suggestions: string[];
}

// =============================================================================
// REQUEST TYPES
// =============================================================================

/**
 * Employee data for validation request.
 */
export interface EmployeeValidationData {
  employee_ni_number: string;
  last_name: string;
  first_name: string;
  middle_initial?: string;
  original_gross_wages: number;
  original_gratuity?: number;
  weeks_worked: number;
  category?: number;
  pay_indicator?: 'W' | 'B' | 'S' | 'M';
}

/**
 * Request to validate a full submission.
 */
export interface ValidateSubmissionRequest {
  employer_ni: string;
  contribution_period: string;
  format_type?: 'HOSPITALITY' | 'NON_HOSPITALITY';
  pay_indicator?: 'W' | 'B' | 'S' | 'M';
  employees: EmployeeValidationData[];
}

/**
 * Request to validate a single NI number.
 */
export interface ValidateNIRequest {
  ni_number: string;
}

/**
 * Request to validate a single employee.
 */
export interface ValidateEmployeeRequest {
  employer_ni?: string;
  contribution_period?: string;
  format_type?: 'HOSPITALITY' | 'NON_HOSPITALITY';
  pay_indicator?: 'W' | 'B' | 'S' | 'M';
  employee: EmployeeValidationData;
}

// =============================================================================
// UI STATE TYPES
// =============================================================================

/**
 * State for tracking acknowledged warnings in the UI.
 */
export interface AcknowledgedWarnings {
  /** Set of warning codes that have been acknowledged */
  acknowledgedCodes: Set<string>;
  /** Set of employee NI numbers whose warnings have been acknowledged */
  acknowledgedEmployees: Set<string>;
}

/**
 * Props for validation action buttons.
 */
export interface ValidationActionProps {
  /** Apply a suggested fix */
  onApplySuggestion?: (
    employeeNi: string,
    field: string,
    value: string
  ) => void;
  /** Edit an employee */
  onEditEmployee?: (employeeNi: string) => void;
  /** Remove an employee from submission */
  onRemoveEmployee?: (employeeNi: string) => void;
  /** Acknowledge a warning */
  onAcknowledgeWarning?: (employeeNi: string, code: string) => void;
}

// =============================================================================
// VALIDATION CODE CONSTANTS
// =============================================================================

/**
 * Standard validation error codes.
 * Matches backend ValidationCodes class.
 */
export const ValidationCodes = {
  // Format layer - NI number
  FORMAT_NI_REQUIRED: 'FORMAT_NI_REQUIRED',
  FORMAT_NI_LENGTH: 'FORMAT_NI_LENGTH',
  FORMAT_NI_DIGITS: 'FORMAT_NI_DIGITS',
  FORMAT_NI_CHECKSUM: 'FORMAT_NI_CHECKSUM',

  // Format layer - Name
  FORMAT_LASTNAME_REQUIRED: 'FORMAT_LASTNAME_REQUIRED',
  FORMAT_LASTNAME_LENGTH: 'FORMAT_LASTNAME_LENGTH',
  FORMAT_FIRSTNAME_REQUIRED: 'FORMAT_FIRSTNAME_REQUIRED',
  FORMAT_FIRSTNAME_LENGTH: 'FORMAT_FIRSTNAME_LENGTH',

  // Format layer - Wages
  FORMAT_WAGES_REQUIRED: 'FORMAT_WAGES_REQUIRED',
  FORMAT_WAGES_NEGATIVE: 'FORMAT_WAGES_NEGATIVE',
  FORMAT_WAGES_INVALID: 'FORMAT_WAGES_INVALID',

  // Format layer - Weeks
  FORMAT_WEEKS_REQUIRED: 'FORMAT_WEEKS_REQUIRED',
  FORMAT_WEEKS_RANGE: 'FORMAT_WEEKS_RANGE',

  // Business layer - Wages
  BUSINESS_WAGES_EXCEED_CEILING: 'BUSINESS_WAGES_EXCEED_CEILING',
  BUSINESS_WAGES_TRUNCATED: 'BUSINESS_WAGES_TRUNCATED',
  BUSINESS_WAGES_ZERO: 'BUSINESS_WAGES_ZERO',

  // Business layer - Category
  BUSINESS_CATEGORY_INVALID: 'BUSINESS_CATEGORY_INVALID',
  BUSINESS_CATEGORY_AGE_MISMATCH: 'BUSINESS_CATEGORY_AGE_MISMATCH',

  // Business layer - Gratuity
  BUSINESS_GRATUITY_NON_HOSPITALITY: 'BUSINESS_GRATUITY_NON_HOSPITALITY',
  BUSINESS_GRATUITY_RETIREE: 'BUSINESS_GRATUITY_RETIREE',

  // Business layer - Period
  BUSINESS_WEEKS_EXCEED_MONTH: 'BUSINESS_WEEKS_EXCEED_MONTH',
  BUSINESS_PERIOD_INVALID: 'BUSINESS_PERIOD_INVALID',

  // Cross-reference layer
  CROSSREF_NI_INVALID_DATABASE: 'CROSSREF_NI_INVALID_DATABASE',
  CROSSREF_NI_DUPLICATE: 'CROSSREF_NI_DUPLICATE',
  CROSSREF_EMPLOYER_INVALID: 'CROSSREF_EMPLOYER_INVALID',
} as const;

export type ValidationCode = keyof typeof ValidationCodes;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if an issue blocks submission.
 */
export function isBlockingIssue(issue: ValidationIssue): boolean {
  return issue.severity === 'ERROR';
}

/**
 * Check if an issue requires acknowledgment.
 */
export function requiresAcknowledgment(issue: ValidationIssue): boolean {
  return issue.severity === 'WARNING';
}

/**
 * Get all errors from a validation result.
 */
export function getErrors(result: EmployeeValidationResult): ValidationIssue[] {
  return result.issues.filter(issue => issue.severity === 'ERROR');
}

/**
 * Get all warnings from a validation result.
 */
export function getWarnings(
  result: EmployeeValidationResult
): ValidationIssue[] {
  return result.issues.filter(issue => issue.severity === 'WARNING');
}

/**
 * Get all info messages from a validation result.
 */
export function getInfoMessages(
  result: EmployeeValidationResult
): ValidationIssue[] {
  return result.issues.filter(issue => issue.severity === 'INFO');
}

/**
 * Get severity color class for Tailwind styling.
 */
export function getSeverityColor(severity: ValidationSeverity): {
  bg: string;
  text: string;
  border: string;
  icon: string;
} {
  switch (severity) {
    case 'ERROR':
      return {
        bg: 'bg-red-50',
        text: 'text-red-800',
        border: 'border-red-200',
        icon: 'text-red-500',
      };
    case 'WARNING':
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-800',
        border: 'border-amber-200',
        icon: 'text-amber-500',
      };
    case 'INFO':
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-800',
        border: 'border-blue-200',
        icon: 'text-blue-500',
      };
  }
}
